import { FunctionComponent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons';
import { InventoryMovement, Product } from '@spike/product-model';
import { ProductHeader } from './ProductHeader';
import { getMovementReason } from './StockHistory';
import styles from './StockSidebar.module.scss';

interface StockHistoryDetailsProps {
    product: Product;
    movement: InventoryMovement;
    onBack: () => void;
}

const formatMovementDate = (movement: InventoryMovement): string => {
    if (!movement.createdAt || !movement.createdAt.isValid()) {
        return '-';
    }

    return movement.createdAt.format('MMM D, YYYY · h:mma').replace('AM', 'am').replace('PM', 'pm');
};

const formatDelta = (delta: number): string => {
    if (delta > 0) {
        return `+${delta}`;
    }

    return String(delta);
};

const formatUnitCost = (value: number | undefined): string => {
    if (value === undefined || Number.isNaN(Number(value))) {
        return '-';
    }

    return `$${Number(value).toFixed(2)}`;
};

export const StockHistoryDetails: FunctionComponent<StockHistoryDetailsProps> = ({ product, movement, onBack }) => {
    return (
        <>
            <div className={styles.detailsHeader}>
                <button
                    type="button"
                    className={styles.backButton}
                    onClick={onBack}
                    aria-label="Back to stock history"
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
                <h2 className={styles.detailsTitle}>Stock History Details</h2>
            </div>
            <ProductHeader
                product={product}
                trackingLabel="Track Total Stock"
            />
            <div className={styles.metrics}>
                <div className={styles.metricCard}>
                    <span className={styles.metricLabel}>Qty Adjusted</span>
                    <span className={styles.metricValue}>{formatDelta(movement.quantity.delta)}</span>
                </div>
                <div className={styles.metricCard}>
                    <span className={styles.metricLabel}>Stock After Adjustment</span>
                    <span className={styles.metricValue}>{movement.quantity.after.product}</span>
                </div>
            </div>
            <div className={styles.detailsList}>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Date</span>
                    <span className={styles.detailValue}>{formatMovementDate(movement)}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Staff</span>
                    <span className={styles.detailValue}>{movement.staff?.name || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Reason</span>
                    <span className={styles.detailValue}>{getMovementReason(movement) || '-'}</span>
                </div>
                <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Unit Cost</span>
                    <span className={styles.detailValue}>{formatUnitCost(movement.unitCost)}</span>
                </div>
            </div>
        </>
    );
};

export default StockHistoryDetails;
